/**
 * One place: the full card.
 *
 * The layout is a 2-column Bento in TripPool's Cartoon / Neo-brutalism visual language.
 *
 * Left Column (~60%):
 * 1. Saved Post Source card (platform badge, author handle/host, outbound link to original post)
 * 2. Notes & Details card (best time metric, cartoon suitability stickers, the note with hashtags, amenities)
 *
 * Right Column (~40%):
 * 3. Map & Location card (specific address, interactive vector place map, coordinates bar)
 * 4. Trip Planning CTA card (encouraging the user to add the place to an itinerary)
 *
 * The visual language changed; the product's central rule did not, and it is the reason anything on
 * this screen can be trusted. A place's attributes are three-valued, not two:
 *
 * - **present** - the user recorded it,
 * - **`null`** - the user did not record it,
 * - **`false`/`0`** - the user recorded a negative.
 *
 * `spec §1.2` requires those to render differently, and the stickers below are that rule made visible:
 * a recorded "no" keeps the red sticker and the words "không phù hợp", while an unanswered question
 * gets the grey sticker and "chưa xác định". Showing "không phù hợp với trẻ em" for an answer the user
 * never gave invents that answer, and the planner then filters on it. So no nullable field is rendered
 * as `{value}` or through a `??` that would collapse the missing answer into the negative; `flag()`
 * below is the one place that decides which of the three states a sticker draws.
 *
 * The sources are shown with their credit line (AC-5.2). That matters beyond attribution: the MVP
 * never fetches a source URL (ADR-004), so the link is the *only* way back to where the user found the
 * place - and a place whose origin cannot be found again is barely better than one not saved.
 *
 * Editing hands over to the capture screen in edit mode (`/capture?place=<id>`), which owns the form;
 * this screen only has to reach it. Deleting leaves at once: a card for a place that no longer exists
 * is a dead end, and the delete's own response is the last chance to say what it broke.
 */

import { Suspense, lazy } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Source } from "@trippool/contracts";

import { copy } from "../copy.ts";
import { isUsableCoordinate } from "../lib/geo.ts";
import { useGoBack } from "../lib/useGoBack.ts";
import { useToast } from "../components/Toast.tsx";
import { deleteReport } from "./placeDeletion.ts";
import { messageFor } from "../lib/errors.ts";
import { useDeletePlace, usePlace } from "../lib/queries.ts";
import { useCurrentUser } from "../lib/session-context.tsx";

const VectorPlaceMap = lazy(async () => {
  const module = await import("../components/VectorPlaceMap.tsx");
  return { default: module.VectorPlaceMap };
});

/*
 * There is no distance line on this screen, and this note is why so that the next phase does not put
 * one back naively.
 *
 * The rule: a fixed landmark only works as the centre of the area the place is actually in - Đà Lạt's
 * centre is Chợ Đà Lạt, Phan Thiết's would be that city's own centre. One global constant is therefore
 * wrong for every place outside the single city it names, and the constant that used to sit here was
 * wrong even for Đà Lạt: it was the seed coordinate of a *different* place (11.9404, 108.4583), 2.29 km
 * from the real Chợ Đà Lạt. Deciding how to determine an area's centre is unsolved, so the screen
 * shows no distance at all rather than one that is confidently wrong.
 */

/**
 * A nullable boolean as one of its three states, drawn as a suitability sticker.
 *
 * The rule the file header states, enforced in one place instead of four copies of a ternary. `null`
 * is "the user did not record it" - grey, and the words "chưa xác định". `false` is a recorded
 * negative and keeps the red sticker and "không phù hợp", because a "no" the user deliberately
 * recorded is information, and rendering it as unknown would throw that information away. The
 * modifier, the glyph and the status are chosen together here, so a missing answer cannot be rendered
 * as a negative by accident.
 *
 * The name, the glyph pair and the two labels are arguments because the four questions do not share
 * their words: kids, elderly and pets answer with "phù hợp" / "không phù hợp", while indoor answers
 * with "trong nhà" / "ngoài trời".
 */
function flag(
  value: boolean | null,
  name: string,
  glyphs: { yes: string; no: string },
  labels: { yes: string; no: string },
): ReactElement {
  const modifier =
    value === null
      ? "place-detail__sticker--unknown"
      : value
        ? "place-detail__sticker--yes"
        : "place-detail__sticker--no";
  return (
    <div className={`place-detail__sticker ${modifier}`}>
      <span className="place-detail__sticker-icon" aria-hidden="true">
        {value === null ? "❓" : value ? glyphs.yes : glyphs.no}
      </span>
      <span className="place-detail__sticker-name">{name}</span>
      <span className="place-detail__sticker-status">
        {value === null ? copy.state.unknown : value ? labels.yes : labels.no}
      </span>
    </div>
  );
}

/** Formatted platform details detected from a source URL. */
interface PlatformInfo {
  name: string;
  icon: string;
  handleOrHost: string;
}

/** Detects platform and display identity from URL. */
function detectPlatform(url: string | null): PlatformInfo {
  if (!url) {
    return { name: "Thủ công", icon: "📝", handleOrHost: "Ghi chú cá nhân" };
  }
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host.includes("tiktok.com")) {
      const match = parsed.pathname.match(/@([^/]+)/);
      const handle = match ? `@${match[1]}` : host;
      return { name: "TikTok", icon: "🎵", handleOrHost: handle };
    }
    if (host.includes("facebook.com") || host.includes("fb.watch")) {
      return { name: "Facebook", icon: "📘", handleOrHost: host };
    }
    if (host.includes("instagram.com")) {
      return { name: "Instagram", icon: "📸", handleOrHost: host };
    }
    if (host.includes("google.com/maps") || host.includes("maps.app.goo.gl") || host.includes("goo.gl")) {
      return { name: "Google Maps", icon: "📍", handleOrHost: host };
    }
    return { name: "Trang web", icon: "🌐", handleOrHost: host };
  } catch {
    return { name: "Liên kết", icon: "🔗", handleOrHost: url };
  }
}

/** Formats best time slots. */
function formatBestTime(bestTime: readonly string[]): string {
  if (bestTime.length === 0) return copy.state.unknown;
  return bestTime.join(", ");
}

/** Outbound Google Maps directions URL. */
function googleMapsDirectionsUrl(location: { lat: number; lon: number } | null): string | null {
  if (!isUsableCoordinate(location)) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${location.lat},${location.lon}`;
}

/** Outbound Google Maps search/view URL. */
function googleMapsSearchUrl(location: { lat: number; lon: number } | null): string | null {
  if (!isUsableCoordinate(location)) return null;
  return `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lon}`;
}

/** OpenStreetMap outbound URL. */
function openStreetMapUrl(location: { lat: number; lon: number } | null): string | null {
  if (!isUsableCoordinate(location)) return null;
  const { lat, lon } = location;
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;
}

/** A URL reduced to the site it points at. */
function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** One source row with resolved labels. */
interface SourceRow {
  id: string;
  label: string;
  url: string | null;
  host: string | null;
}

function sourceRows(sources: readonly Source[]): SourceRow[] {
  const rows: SourceRow[] = [];
  for (const source of sources) {
    const attribution = source.attribution?.trim() ?? "";
    const url = source.url?.trim() ?? "";
    if (attribution === "" && url === "") continue;
    const host = url === "" ? null : sourceHost(url);
    rows.push({
      id: source.id,
      label: attribution === "" ? (host ?? "") : attribution,
      url: url === "" ? null : url,
      host,
    });
  }
  return rows;
}

/** Formats ISO timestamp to Vietnamese friendly date string. */
function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return copy.state.unknown;
  return new Intl.DateTimeFormat("vi-VN", { dateStyle: "long", timeStyle: "short" }).format(date);
}

/** Renders notes text with highlighted hashtags. */
function renderDescription(desc: string): ReactElement {
  const words = desc.split(/(\s+)/);
  return (
    <div className="place-detail__desc">
      {words.map((word, index) => {
        if (word.startsWith("#") && word.length > 1) {
          return (
            <span key={index} className="place-detail__hashtag">
              {word}
            </span>
          );
        }
        return word;
      })}
    </div>
  );
}

export function PlaceDetailScreen(): ReactElement {
  const user = useCurrentUser();
  const userId = user?.id ?? "";
  const { placeId = "" } = useParams();
  const place = usePlace(userId, placeId);
  const deletePlace = useDeletePlace(userId);
  const navigate = useNavigate();
  const goBack = useGoBack("/places");
  const { showToast } = useToast();

  async function onDelete(): Promise<void> {
    if (card === undefined) return;
    if (!globalThis.confirm(copy.places.confirmRemove(card.name))) return;
    let response;
    try {
      response = await deletePlace.mutateAsync(card.id);
    } catch {
      return;
    }
    showToast(deleteReport(card.name, response));
    void navigate("/places");
  }

  if (place.isPending) return <p className="state">{copy.state.loading}</p>;

  if (place.isError) {
    return (
      <div className="state state--error" role="alert">
        <p>{messageFor(place.error)}</p>
        {/*
          A button rather than the `<Link to="/places">` this was — the last control in the app that still
          named a fixed destination while wearing the word "back". A place can be opened from Home, from the
          planner or from the list, and the error state is reached by going back into a place that has since
          been deleted, so the honest thing is to come back the way the user went in; `useGoBack` keeps the
          list as the fallback for a page that was opened rather than navigated to.
        */}
        <button type="button" className="link" onClick={goBack}>
          {copy.placeDetail.backShort}
        </button>
      </div>
    );
  }

  const card = place.data;
  const location = card.location;
  const mapUrl = openStreetMapUrl(location);
  const directionsUrl = googleMapsDirectionsUrl(location);
  const googleSearchUrl = googleMapsSearchUrl(location);
  const sources = sourceRows(card.sources);
  const primarySource = sources[0] ?? null;
  const primaryPlatform = detectPlatform(primarySource?.url ?? null);

  return (
    <div className="screen place-detail">
      {/* Header: Navigation, Breadcrumb & Action Controls */}
      <header className="place-detail__header">
        <div className="place-detail__nav">
          <button type="button" className="place-detail__back" onClick={goBack}>
            <span aria-hidden="true">←</span> {copy.placeDetail.backShort}
          </button>

          <div className="place-detail__actions">
            <Link className="button button--ghost" to={`/capture?place=${card.id}`}>
              <span aria-hidden="true">✏️</span> {copy.places.edit}
            </Link>
            <button
              type="button"
              className="button button--danger"
              onClick={() => void onDelete()}
              disabled={deletePlace.isPending}
            >
              <span aria-hidden="true">🗑️</span> {copy.places.remove}
            </button>
          </div>
        </div>

        {deletePlace.isError && (
          <p className="state state--error" role="alert">
            {messageFor(deletePlace.error)}
          </p>
        )}

        {/* Hero Banner: Meta Chips above Title */}
        <div className="place-detail__hero-banner">
          <div className="place-detail__meta-row">
            {card.categories.map((cat) => (
              <span key={cat} className="place-detail__cat-badge">
                🏷️ {cat}
              </span>
            ))}
            {card.subcategory !== null && card.subcategory.trim() !== "" && (
              <span className="place-detail__cat-badge">
                {card.subcategory}
              </span>
            )}
            {card.areas.map((area) => (
              <span key={area.id} className="place-detail__area-badge">
                📍 {area.name}
              </span>
            ))}
            {location !== null && mapUrl !== null ? (
              <a
                className="place-detail__coords-pill link"
                href={mapUrl}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={copy.placeDetail.coordinatesOnMap(
                  location.lat.toFixed(5),
                  location.lon.toFixed(5),
                )}
              >
                🌐 {location.lat.toFixed(5)}, {location.lon.toFixed(5)}
              </a>
            ) : (
              <span className="place-detail__no-coords">
                ⚠ {copy.placeDetail.noCoordinatesConsequence}
              </span>
            )}
          </div>

          <div className="place-detail__title-row">
            <h1 className="place-detail__title">{card.name}</h1>
            <div className="place-detail__timestamp">
              🕒 {copy.placeDetail.savedAt(formatWhen(card.created_at))}
            </div>
          </div>
        </div>
      </header>

      {/* 2-Column Bento Grid: Left ~60% (Story & Notes), Right ~40% (Map & Planning) */}
      <div className="place-detail__bento">
        {/* Left Column: Content & Notes */}
        <div className="place-detail__col-content">
          {/* 1. Nguồn bài viết đã lưu Card */}
          <section className="place-detail__card place-detail__source-card">
            <div className="place-detail__card-header">
              <div className="place-detail__card-title-group">
                <h2 className="place-detail__card-title">{copy.placeDetail.savedPostSource}</h2>
                <p className="place-detail__card-subtitle">
                  {sources.length > 0 ? copy.placeDetail.savedViaLink : copy.placeDetail.savedManual}
                </p>
              </div>
              {primarySource?.url ? (
                <span className="place-detail__platform-tag">
                  {primaryPlatform.icon} {primaryPlatform.name}
                </span>
              ) : (
                <span className="place-detail__platform-tag">
                  📝 {copy.placeDetail.savedManual}
                </span>
              )}
            </div>

            {sources.length > 0 && (
              <div className="place-detail__source-body">
                {sources.map((source) => {
                  const platform = detectPlatform(source.url);
                  return (
                    <div key={source.id} className="place-detail__source-item">
                      <div className="place-detail__source-identity">
                        <span className="place-detail__source-platform-icon" aria-hidden="true">
                          {platform.icon}
                        </span>
                        <div className="place-detail__source-meta">
                          <span className="place-detail__source-name">{source.label}</span>
                          <span className="place-detail__source-url">{platform.handleOrHost}</span>
                        </div>
                      </div>
                      {source.url !== null && (
                        <a
                          className="button button--ghost place-detail__source-cta"
                          href={source.url}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {copy.placeDetail.openOriginalPost(platform.name)}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* 2. Ghi chú & Chi tiết đã nhập Card */}
          <section className="place-detail__card place-detail__notes-card">
            <div className="place-detail__card-header">
              <h2 className="place-detail__card-title">{copy.placeDetail.notesAndDetailsHeading}</h2>
              <Link className="place-detail__header-link" to={`/capture?place=${card.id}`}>
                ✏️ {copy.placeDetail.editNotesAction}
              </Link>
            </div>

            {/* Metrics Row: Best Time */}
            <div className="place-detail__metrics-grid">
              <div className="place-detail__metric-tile">
                <span className="place-detail__metric-label">🕒 {copy.capture.bestTimeLabel}</span>
                <span className="place-detail__metric-value">{formatBestTime(card.best_time)}</span>
              </div>
            </div>

            {/* Suitability Matrix / Stickers */}
            <div className="place-detail__suitability-block">
              <h3 className="place-detail__section-subtitle">{copy.placeDetail.suitabilityHeading}</h3>
              <div className="place-detail__stickers-grid">
                {flag(card.kids_friendly, copy.placeDetail.kids, { yes: "✓", no: "✕" }, {
                  yes: copy.placeDetail.suitable,
                  no: copy.placeDetail.notSuitable,
                })}
                {flag(card.elderly_friendly, copy.placeDetail.elderly, { yes: "✓", no: "✕" }, {
                  yes: copy.placeDetail.suitable,
                  no: copy.placeDetail.notSuitable,
                })}
                {flag(card.pet_friendly, copy.placeDetail.pets, { yes: "✓", no: "✕" }, {
                  yes: copy.placeDetail.suitable,
                  no: copy.placeDetail.notSuitable,
                })}
                {flag(card.is_indoor, copy.placeDetail.indoor, { yes: "🏠", no: "🌳" }, {
                  yes: copy.placeDetail.indoorYes,
                  no: copy.placeDetail.indoorNo,
                })}
              </div>
            </div>

            {/* Opening hours & Duration if recorded */}
            {(card.opening_hours !== null || card.estimated_duration_min !== null) && (
              <div className="place-detail__specs-row">
                {card.opening_hours !== null && (
                  <div className="place-detail__spec-item">
                    <span className="place-detail__spec-label">⏰ {copy.placeDetail.openingHours}:</span>
                    <span className="place-detail__spec-value">
                      {card.opening_hours.open} - {card.opening_hours.close}
                    </span>
                  </div>
                )}
                {card.estimated_duration_min !== null && (
                  <div className="place-detail__spec-item">
                    <span className="place-detail__spec-label">⏱ {copy.placeDetail.duration}:</span>
                    <span className="place-detail__spec-value">{card.estimated_duration_min} phút</span>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div className="place-detail__notes-block">
              {card.description !== null && card.description.trim() !== "" ? (
                renderDescription(card.description)
              ) : (
                <p className="place-detail__notes-empty">{copy.placeDetail.notesEmpty}</p>
              )}
            </div>

            {/* Amenities Chips */}
            {card.amenities.length > 0 && (
              <div className="place-detail__amenities-block">
                <span className="place-detail__amenities-label">✨ {copy.placeDetail.amenities}:</span>
                <div className="chips">
                  {card.amenities.map((amenity) => (
                    <span key={amenity} className="chip">
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Right Column: Map, Location & Planning */}
        <div className="place-detail__col-aside">
          {/* 3. Map & Location Card */}
          <section className="place-detail__card place-detail__map-card">
            <div className="place-detail__card-header">
              <h2 className="place-detail__card-title">{copy.placeDetail.mapAndLocationHeading}</h2>
              {directionsUrl !== null && (
                <a
                  className="place-detail__header-link"
                  href={directionsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  🧭 {copy.placeDetail.directionsAction}
                </a>
              )}
            </div>

            {/* Address */}
            <div className="place-detail__address-block">
              <div className="place-detail__address-label">{copy.placeDetail.specificAddress}</div>
              <div className="place-detail__address-text">
                {card.address ?? copy.placeDetail.noAddressYet}
              </div>
            </div>

            {/* Map Preview */}
            <div className="place-detail__map-frame">
              {location !== null ? (
                <Suspense fallback={<p className="field__help">{copy.state.loading}</p>}>
                  <VectorPlaceMap compact name={card.name} location={location} />
                </Suspense>
              ) : (
                <div className="place-detail__map-empty">
                  <span aria-hidden="true">🗺️</span>
                  <p>{copy.placeDetail.noCoordinatesConsequence}</p>
                </div>
              )}
            </div>

            {/* Coordinates Bar */}
            {location !== null && (
              <div className="place-detail__coords-bar">
                <span className="place-detail__coords-val">
                  ● {location.lat.toFixed(4)}° N, {location.lon.toFixed(4)}° E
                </span>
                {googleSearchUrl !== null && (
                  <a
                    className="place-detail__coords-link"
                    href={googleSearchUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {copy.placeDetail.openGoogleMaps}
                  </a>
                )}
              </div>
            )}
          </section>

          {/* 4. Plan CTA Card */}
          <section className="place-detail__card place-detail__plan-card">
            <div className="place-detail__plan-icon" aria-hidden="true">
              🧭
            </div>
            <div className="place-detail__plan-info">
              <h2 className="place-detail__plan-title">{copy.placeDetail.planningHeading}</h2>
              <p className="place-detail__plan-intro">{copy.placeDetail.planningIntro}</p>
            </div>
            <Link className="button button--primary place-detail__plan-btn" to="/trips">
              ➕ {copy.placeDetail.addToTripButton}
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}
